import { ControlledAutocomplete } from 'Components';
import { ICriteriaStyle } from '..';
import { Avatar, FilterOptionsState, createFilterOptions } from '@mui/material';
import { AccordionArrowPurple } from 'Images';
import { useEffect, useRef, useState } from 'react';
import {
  longestResourceOption,
  transformCitiesToStringArray,
} from 'Utils/suppliersResources';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { supplierAutoCompleteTitleWidthHandler } from 'Utils/supplierFilterManager';
import { DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT } from 'Config';
import { infiniteScrollLoaderHandler } from 'Utils';
import { CustomCitiesResult } from './CustomCitiesResult';
import { useSupplierContext } from 'Hooks/useSupplierContext';
import { useSuppliersDispatchType } from 'Hooks/useSuppliersDispatchType';

const halfResultScrollJumpSize = DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT / 2;

interface ISelectedCities {
  names: string[];
  ids: number[];
}

export const Location = ({
  autoCompleteSx,
  autoCompletePaperSx,
}: ICriteriaStyle) => {
  const { filters, setFilters, dispatchSuppliers } = useSupplierContext();
  const dispatchType = useSuppliersDispatchType();

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage;

  const [selectedCities, setSelectedCities] = useState<ISelectedCities>({
    names: [],
    ids: [],
  });
  const [scrollIndex, setScrollIndex] = useState<number>(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const citiesOptions: string[] = transformCitiesToStringArray(cities);

  useEffect(() => {
    let updatedFilter = { ...filters };
    if (filters.city) {
      const citiesName: string[] = [],
        citiesId: number[] = [];
      for (let i = 0; i < filters.city.length; i++) {
        const city = cities?.find((city) => filters.city![i] === city.id);
        if (city) {
          citiesName.push(city.value);
          citiesId.push(city.id);
        }
      }
      setSelectedCities({ names: citiesName, ids: citiesId });
      updatedFilter = {
        ...updatedFilter,
        city: [...citiesId],
      };
    }

    dispatchSuppliers({
      type: dispatchType,
      payload: { value: updatedFilter },
    });
  }, [
    filters.city,
    cities,
    dispatchSuppliers,
    selectedCities.ids.length,
    dispatchType,
  ]);

  useEffect(() => {
    if (listRef.current) {
      const list = listRef.current.children[0];
      list.scrollTo({ top: list.scrollHeight / 2 - 100 });
    }
  }, [scrollIndex]);

  const selectedCitiesHandler = (cityValue: string) => {
    let updatedFilter = { ...filters };
    const selectedCity = cities?.find((city) => city.value === cityValue);
    if (selectedCity && cityValue) {
      const index = selectedCities.names.indexOf(cityValue);
      if (index >= 0) {
        selectedCities.names.splice(index, 1);
        selectedCities.ids.splice(index, 1);
        setSelectedCities({
          names: selectedCities.names,
          ids: selectedCities.ids,
        });
        updatedFilter = {
          ...updatedFilter,
          city: [...selectedCities.ids],
        };
      } else {
        setSelectedCities({
          names: [...selectedCities.names, selectedCity.value],
          ids: [...selectedCities.ids, selectedCity.id],
        });
        updatedFilter = {
          ...updatedFilter,
          city: [...selectedCities.ids, selectedCity.id],
        };
      }
      setFilters(updatedFilter);
    }
    dispatchSuppliers({
      type: dispatchType,
      payload: { value: updatedFilter },
    });
  };

  const resetSelectedCitiesHandler = () => {
    const updatedFilter = { ...filters, city: [] };
    setSelectedCities({
      names: [],
      ids: [],
    });
    setFilters(updatedFilter);
    dispatchSuppliers({
      type: dispatchType,
      payload: { value: updatedFilter },
    });
  };

  const locationValueHandler = () => {
    const cities = selectedCities.names;
    if (cities.length === 0) return dictionary.therapy.allCities;
    if (cities.length === 1) return cities[0];
    return `${dictionary.therapy.chosen} ${cities.length} ${dictionary.therapy.location}`;
  };

  const customFilterOptions = (
    options: string[],
    state: FilterOptionsState<string>
  ) => {
    const inputValue = state.inputValue;
    if (!options.some((option) => option.includes(inputValue))) {
      state.inputValue = '';
    }

    let MaxViewList: string[] = [];
    const filtered = createFilterOptions<string>({})(options, state);

    MaxViewList = [...filtered];
    if (state.inputValue.length > 0) {
      MaxViewList = MaxViewList.slice(0, DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT);
    } else {
      if (
        scrollIndex * DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT >
        options.length - DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT
      )
        MaxViewList = MaxViewList.slice(
          scrollIndex * DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT -
            DEFAULT_MAX_AUTOCOMPLETE_RESULT_LIMIT,
          options.length - 1
        );
      else
        MaxViewList = MaxViewList.slice(
          scrollIndex * halfResultScrollJumpSize,
          (scrollIndex + 1) * halfResultScrollJumpSize +
            halfResultScrollJumpSize
        );
    }

    return MaxViewList.filter(
      (city: string) => !selectedCities.names.includes(city)
    );
  };

  return (
    <ControlledAutocomplete
      label=''
      options={citiesOptions}
      filterOptions={customFilterOptions}
      noResultsText={dictionary.noResults}
      isCheckbox
      selectedCheckboxes={selectedCities.names}
      value={locationValueHandler()}
      setValue={() => {}}
      onClickCheckbox={selectedCitiesHandler}
      resetSelectedCheckboxes={resetSelectedCitiesHandler}
      scrollPaperResultHandler={(event) =>
        infiniteScrollLoaderHandler({
          event,
          scrollIndex,
          setScrollIndex,
          optionsLength: citiesOptions.length,
        })
      }
      listRef={listRef}
      style={{
        ...autoCompleteSx,
        '&&& .MuiOutlinedInput-input': {
          p: 0,
          minWidth: '120px',
          width: `${
            supplierAutoCompleteTitleWidthHandler(locationValueHandler()) + 1
          }ch`,
          cursor: 'pointer',
        },
      }}
      paperComponent={
        <CustomCitiesResult
          selectedCities={selectedCities.names}
          selectCity={selectedCitiesHandler}
        />
      }
      paperSx={{
        ...autoCompletePaperSx,
        m: '16px 0 0 -40px',
        p: '0 0 60px 0',
        width: `${longestResourceOption(citiesOptions) + 2}ch`,
        boxShadow: 'none',
        maxHeight:
          selectedCities.names.length === 0
            ? '300px'
            : `${300 + selectedCities.names.length * 50}px`,

        '& .MuiAutocomplete-listbox': {
          maxHeight: '300px',
        },
      }}
      endAdornment={
        isDesktop ? (
          <Avatar
            alt=''
            src={AccordionArrowPurple}
            sx={{ width: '30px', height: '30px' }}
          />
        ) : null
      }
      endAdornmentActiveTransformations='rotate(180deg)'
      disableClearIcon
    />
  );
};
